/**
 * src/contexts/HexclaveAuthProvider.tsx — the **only** auth provider.
 *
 * Plan: `docs/PLAN-STACKAUTH.md` §7 (frontend), §9 (the authorised behaviour changes), §10 step 3.
 *
 * Supplies `AuthContext`, so `ProtectedRoute`, `ConverterApp`, `EnhancedSavedDocuments`, `Admin.tsx`
 * and every `use-database` hook are untouched by the migration — they consume `useAuth()` and never
 * learn where the user came from. The password-based `AuthProvider` it used to sit beside was
 * deleted on 2026-08-09 (owner instruction); this is no longer one of two.
 *
 * ═══ WHERE `user` COMES FROM, AND WHY IT IS NOT HEXCLAVE'S USER OBJECT ═════════════════════════
 *
 * The app's `User` type is the **D1** row: `id`, `email`, `username`, `role`, `is_approved`. Hexclave
 * knows none of the last two, and `id` must be the D1 integer because `use-database`'s query keys and
 * every `user_id` foreign key are built from it.
 *
 * So this provider does exactly what the legacy one did: it calls **`GET /api/auth/me`**, which the
 * Worker answers from D1. Hexclave's own user object is used for one thing only — deciding whether a
 * session exists at all. Reading `role` from Hexclave would let the identity provider's configuration
 * grant admin over the scripture corpus, which `worker/lib/middleware.ts` refuses on the server side
 * and which must not sneak back in here.
 *
 * ═══ DELIBERATELY PRESERVED DEFECTS ════════════════════════════════════════════════════════════
 *  - **DF-22 rank 18 — `logout()` does not clear the React Query cache.** This file does not import
 *    `useQueryClient`, exactly as `AuthContext.tsx` does not, so nobody can add it by reflex. Do not
 *    add `queryClient.clear()` here.
 *  - No error boundary, no global 401/403 interceptor (DF-22 ranks 5 and 6).
 *
 * ═══ WHAT IS GENUINELY DIFFERENT, AND AUTHORISED (plan §9) ═════════════════════════════════════
 *  - **`login` and `register` no longer exist**, on the context or anywhere else. The hosted pages
 *    own sign-in, sign-up, password reset and email verification. An earlier revision kept them as
 *    methods that threw; that is worse than removing them, because a stale call site still compiles.
 *  - `logout` redirects to Hexclave's sign-out, which ends the session on their side too — the
 *    deleted legacy version only dropped a localStorage key, so the session outlived the logout.
 */
import { useCallback, useEffect, useMemo, useState, type FC, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useUser } from '@hexclave/react'
import { AuthContext, type AuthContextType } from './AuthContext'
import { authApi } from '@/lib/api'
import { getHexclaveToken, hexclaveApp } from '@/lib/hexclave'
import type { User } from '@/lib/types'

interface Props {
  children: ReactNode
}

export const HexclaveAuthProvider: FC<Props> = ({ children }) => {
  // `or: 'return-null'` — never redirect from here. `ProtectedRoute` decides what an unauthenticated
  // visitor sees, and it renders `<AuthForm/>` **in place** without changing the URL (spec §1.4.2).
  // A redirect here would break that and take `/admin`'s self-gating with it.
  const hexclaveUser = useUser({ or: 'return-null' })

  /**
   * ═══ THE EFFECT DEPENDS ON THIS STRING, NEVER ON THE USER OBJECT ═══════════════════════════════
   * **This caused an infinite loading spinner in production (2026-08-09).** The dependency array
   * used to read `[hexclaveUser?.id, hexclaveUser]` — the object included alongside the id, despite
   * a comment directly above it saying to use the id *instead of* the object.
   *
   * `useUser()` returns a **fresh object reference on every render**, so:
   *
   *   effect runs → setIsLoading(true) → re-render → new object → deps "changed" → effect runs → …
   *
   * `isLoading` was therefore re-set to `true` on every pass and `ProtectedRoute` painted its
   * full-screen spinner for ever. Worse, each re-run's cleanup set `cancelled = true` on the
   * previous one, so when `GET /api/auth/me` did return, `setIsLoading(false)` was skipped as stale.
   * The loop also called the API on every iteration.
   *
   * Keeping it a primitive is what makes the effect run once per session rather than per render.
   * **Do not put `hexclaveUser` back in the dependency array.**
   */
  const hexclaveUserId = hexclaveUser?.id ?? null

  const [user, setUser] = useState<User | null>(null)
  // Starts true, exactly as the legacy provider does — that is what paints `ProtectedRoute`'s
  // full-screen spinner while `/api/auth/me` is in flight (`AuthContext.tsx:76-79`).
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (hexclaveUserId === null) {
        // Signed out of Hexclave: there is no D1 row to fetch and nothing to keep.
        if (!cancelled) {
          setUser(null)
          setIsLoading(false)
        }
        return
      }

      // `getHexclaveToken` reads the current user from the SDK itself, so the effect needs no
      // reference to `hexclaveUser` — only to know *whether* there is a session, which the id says.
      const token = await getHexclaveToken()
      // `getCurrentUser` never rejects — it swallows network errors and returns null — so a Worker
      // outage at load surfaces as "signed out" rather than an unhandled rejection. Same shape as
      // the legacy provider's restore path, and the same DF-22 rank 5 family of behaviour.
      const current = token ? await authApi.getCurrentUser(token) : null

      if (!cancelled) {
        setUser(current)
        setIsLoading(false)
      }
    }

    setIsLoading(true)
    void load()
    return () => {
      cancelled = true
    }
    // Re-runs only when the session actually appears or disappears — see `hexclaveUserId` above.
  }, [hexclaveUserId])

  /**
   * ⚠️ DF-22 rank 18 is REPRODUCED: the React Query cache is **not** cleared. See the file header.
   *
   * Unlike the legacy `logout`, this ends the session at Hexclave rather than merely dropping a local
   * key — so the tokens are genuinely revoked, not just forgotten (plan §9).
   */
  const logout = useCallback((): void => {
    setUser(null)
    toast('Logged out', { description: 'You have been successfully logged out' })
    void hexclaveApp().redirectToSignOut()
  }, [])

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isLoading,
      // Derived, never stored — and it requires the **D1** row, not merely a Hexclave session. An
      // unapproved user has a valid Hexclave session and no usable account: `/api/auth/me` answers
      // 403 through `hexclaveAuth`, `getCurrentUser` maps that to null, and they correctly read as
      // not authenticated, landing on `AuthForm` with its pending-approval message.
      isAuthenticated: !!user,
      logout,
    }),
    [user, isLoading, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
